import type {QueryClient} from '@github-ui/react-query'
import type {BaseTask, OptimisticTask, UITask, UnhydratedTask} from '../types/task'
import type {APITask, HydratedAPITask} from '../types/api-task'
import {isSessionState, type SessionState} from '../types/session'
import {getGitHubArtifactData} from './task-artifact-helpers'

/**
 * Extracts the updated_at timestamp from an API task, supporting both
 * the new `updated_at` field (copilot_task_api_github_rest_style) and
 * the legacy `last_updated_at` field.
 */
export function getAPITaskUpdatedAt(apiTask: APITask): string {
  // One of these is always present depending on the feature flag
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (apiTask.updated_at ?? apiTask.last_updated_at)!
}

/**
 * Extracts the task state from an API task, supporting both
 * the new `state` field (copilot_task_api_github_rest_style) and
 * the legacy `status` field.
 */
export function getAPITaskState(apiTask: APITask): string {
  return apiTask.state ?? apiTask.status
}

/**
 * Gets the display title for a task.
 *
 * @param task - The task to get the title for
 * @param pullTitle - Optional pull request title to use as fallback
 * @param options - Optional settings
 * @param options.preferPullTitle - When true, prefer pullTitle over task.name (e.g. for code reviews)
 * @returns The display title for the task
 */
export function getTaskTitle(
  task?: UITask | APITask,
  pullTitle?: string,
  options?: {preferPullTitle?: boolean; formatAsReview?: boolean},
): string {
  if (options?.preferPullTitle && pullTitle) {
    return options.formatAsReview ? `Review of ${pullTitle}` : pullTitle
  }

  if (task?.name) {
    return task.name
  }

  if (pullTitle) {
    return options?.formatAsReview ? `Review of ${pullTitle}` : pullTitle
  }

  return 'New task'
}

/**
 * Transforms a single API Task to UnhydratedTask format.
 * Used by the mutation to insert the real task into the cache after creation.
 * Returns null if the task has no GitHub artifact (can't be displayed).
 */
export function transformAPITaskToUnhydrated(apiTask: APITask): UnhydratedTask | null {
  const githubArtifact = getGitHubArtifactData(apiTask.artifacts)
  const firstCollaborator = apiTask.agent_collaborators?.[0]
  const agentId = firstCollaborator?.agent_id?.toString()
  const agentSlug = firstCollaborator?.slug

  return {
    id: apiTask.id,
    resource_type: githubArtifact?.type,
    resource_id: githubArtifact?.id,
    resource_global_id: githubArtifact?.global_id,
    session_count: apiTask.session_count,
    latest_session_state: getAPITaskState(apiTask),
    latest_session_updated_at: getAPITaskUpdatedAt(apiTask),
    agentId,
    agentSlug,
    name: apiTask.name,
    task_id: apiTask.id,
    archived_at: apiTask.archived_at,
    creator_id: apiTask.creator?.id,
    creator_login: apiTask.creator?.login,
    repo_id: apiTask.repository?.id,
    stale_at: apiTask.stale_at,
    custom_agent: apiTask.custom_agent
      ? {
          is_automation: !!apiTask.custom_agent.is_automation,
          name: apiTask.custom_agent.name,
        }
      : undefined,
  } as UnhydratedTask
}

/**
 * Transforms an array of API Tasks to UnhydratedTask format.
 * Filters out tasks that have no GitHub artifact.
 */
export function transformAPITasksToUnhydrated(apiTasks: APITask[]): UnhydratedTask[] {
  return apiTasks.map(transformAPITaskToUnhydrated).filter((task): task is UnhydratedTask => task !== null)
}

/**
 * Merges server-fetched tasks with existing optimistic tasks from the cache.
 *
 * This function handles the transition from optimistic tasks (created client-side before
 * server confirmation) to server tasks:
 * 1. Identifies server tasks that match optimistic tasks via event_identifiers
 * 2. Preserves display metadata from optimistic tasks on their server counterparts
 * 3. Filters out optimistic tasks that now have server equivalents
 * 4. Returns a sorted array of all tasks (newest first)
 *
 * @param serverTasks - Tasks fetched from the server
 * @param optimisticTasks - Optimistic tasks from the local cache
 * @returns Merged and sorted array of tasks
 */
export function mergeServerTasksWithOptimistic(
  serverTasks: UnhydratedTask[],
  optimisticTasks: OptimisticTask[],
): Array<UnhydratedTask | OptimisticTask> {
  // Transform server tasks, marking those that replace optimistic tasks
  const serverTasksWithOptimistic = serverTasks.map(serverTask => {
    const matchingOptimisticTask = optimisticTasks.find(optimisticTask =>
      serverTask.event_identifiers?.includes(optimisticTask.clientGeneratedId),
    )

    return {
      ...serverTask,
      isOptimistic: !!matchingOptimisticTask,
      // Preserve original creation parameters from optimistic task for display
      ...(matchingOptimisticTask && {
        problemStatement: matchingOptimisticTask.problemStatement,
        nwo: matchingOptimisticTask.nwo,
        baseRef: matchingOptimisticTask.baseRef,
        clientGeneratedId: matchingOptimisticTask.clientGeneratedId,
        // Preserve name from optimistic task if server hasn't provided one yet
        name: serverTask.name ?? matchingOptimisticTask.name,
      }),
    }
  })

  // Filter out optimistic tasks that now have server equivalents
  const remainingOptimisticTasks = optimisticTasks.filter(
    optimisticTask =>
      !serverTasksWithOptimistic.some(serverTask =>
        serverTask.event_identifiers?.includes(optimisticTask.clientGeneratedId),
      ),
  )

  // Combine and sort all tasks
  return sortTasksByDate([...serverTasksWithOptimistic, ...remainingOptimisticTasks])
}

/**
 * Task with a latest_session_updated_at timestamp for sorting.
 */
interface TaskWithTimestamp {
  latest_session_updated_at: string
}

/**
 * Sorts tasks by latest_session_updated_at (newest first).
 * This is a generic utility that works with any task type that has a timestamp.
 *
 * @param tasks - Array of tasks to sort
 * @returns Sorted array (does not mutate the original)
 */
export function sortTasksByDate<T extends TaskWithTimestamp>(tasks: T[]): T[] {
  return [...tasks].sort(
    (a, b) => new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime(),
  )
}

/**
 * Shared shape between `FetchTasksResponse` and `FetchRepoTasksResponse` —
 * a tuple of `[tasks, hasNextPage, counts?]`. Typed loosely here to avoid
 * creating a hooks → utils dependency.
 */
type TaskListCacheEntry = [Array<UnhydratedTask | OptimisticTask>, boolean, unknown?]

/**
 * Pushes the latest `stale_at` and `latest_session_state` from a single-task
 * fetch into any cached task lists (`['agent-tasks', ...]` and
 * `['repo-tasks', ...]`) so the lists reflect up-to-date connection status
 * without waiting for their own polling cycle.
 *
 * - No-op when both fields already match (preserves referential equality so
 *   React Query won't trigger needless re-renders).
 * - No-op for any list cache that doesn't include this task id.
 *
 * NOTE: `latest_session_state` is included so list-side connection-status
 * computations see terminal-state transitions immediately. This *can* affect
 * list ordering/bucketing (active vs completed splits derived from session
 * state), but transitions to terminal states are exactly the cases where
 * users want the list to reflect reality immediately.
 */
export function syncTaskListsFromAPITask(queryClient: QueryClient, apiTask: HydratedAPITask): void {
  const taskId = apiTask.id
  const staleAt = apiTask.stale_at
  const sessionState = isSessionState(getAPITaskState(apiTask)) ? (getAPITaskState(apiTask) as SessionState) : undefined
  if (!taskId) return
  if (staleAt === undefined && sessionState === undefined) return

  const updater = (oldData: TaskListCacheEntry | undefined): TaskListCacheEntry | undefined => {
    if (!oldData) return oldData
    const tasks = oldData[0]
    const idx = tasks.findIndex(t => t.id === taskId)
    if (idx === -1) return oldData
    const existing = tasks[idx]
    if (!existing) return oldData
    const staleAtChanged = staleAt !== undefined && existing.stale_at !== staleAt
    const sessionStateChanged = sessionState !== undefined && existing.latest_session_state !== sessionState
    if (!staleAtChanged && !sessionStateChanged) return oldData
    const newTasks = tasks.slice()
    newTasks[idx] = {
      ...existing,
      ...(staleAtChanged ? {stale_at: staleAt} : {}),
      ...(sessionStateChanged ? {latest_session_state: sessionState} : {}),
    } as (typeof tasks)[number]
    return [newTasks, oldData[1], oldData[2]]
  }

  queryClient.setQueriesData<TaskListCacheEntry>({queryKey: ['agent-tasks']}, updater, {updatedAt: Date.now()})
  queryClient.setQueriesData<TaskListCacheEntry>({queryKey: ['repo-tasks']}, updater, {updatedAt: Date.now()})
}

/**
 * Pushes fresh `stale_at` and `latest_session_state` values from a list fetch
 * into any cached single-task queries (`['agent-task', taskId, ...]`) so the
 * detail view reflects updates picked up by the list without waiting for its
 * own poll.
 *
 * - Skips list entries with no `id`.
 * - No-op for any single-task cache where both fields already match.
 *
 * Mirrors {@link syncTaskListsFromAPITask} in the reverse direction.
 */
export function syncSingleTaskCachesFromList(
  queryClient: QueryClient,
  listTasks: ReadonlyArray<Pick<BaseTask, 'id' | 'stale_at' | 'latest_session_state'>>,
): void {
  for (const task of listTasks) {
    const taskId = task.id
    const staleAt = task.stale_at
    const sessionState = task.latest_session_state
    if (!taskId) continue
    if (staleAt === undefined && sessionState === undefined) continue

    queryClient.setQueriesData<HydratedAPITask>(
      {queryKey: ['agent-task', taskId]},
      oldData => {
        if (!oldData) return oldData
        const staleAtChanged = staleAt !== undefined && oldData.stale_at !== staleAt
        const currentState = getAPITaskState(oldData)
        const sessionStateChanged = sessionState !== undefined && currentState !== sessionState
        if (!staleAtChanged && !sessionStateChanged) return oldData
        return {
          ...oldData,
          ...(staleAtChanged ? {stale_at: staleAt} : {}),
          ...(sessionStateChanged ? {state: sessionState, status: sessionState} : {}),
        }
      },
      {updatedAt: Date.now()},
    )
  }
}
